import React from 'react';

import { PrismicVideoEmbed } from 'types/prismicVideo';
import { PrismicLink } from 'types/prismicLink';
import { PrismicRichText } from 'types/prismicRichtext';
import { PrismicImage } from 'types/prismicImage';
import { LinkableFragment } from 'types/prismic';
import {
  PrismicBodyDescriptiveListFields,
  PrismicBodyListFields,
  PrismicCardsFields,
  PrismicPeopleFields,
  PrismicRelatedItemsFields,
  PrismicTabsFields,
  SliceBody,
  SliceCards,
  SliceCta,
  SliceDescriptiveList,
  SliceEmbed,
  SliceHeading,
  SliceIframe,
  SliceImageGallery,
  SliceImageGrid,
  SliceList,
  SlicePartnersList,
  SlicePeople,
  SliceQuote,
  SliceRelated,
  SliceRichtext,
  SliceSingleImage,
  SliceTabs,
  SliceText,
  SliceVideo,
  PrismicImageGridFields,
  PrismicBodyPartnersListFields,
} from 'types/prismicSlices';
import { mapColor } from 'types/placeholderColors';

import { Segment } from 'components/segment/Segment';

import { ImageContainer } from './ImageContainer';
import { GalleryContainer } from './GalleryContainer';
import { TwoColumnContainer } from './TwoColumnContainer';
import { VideoContainer } from './VideoContainer';
import { CtaContainer } from './CtaContainer';
import { QuoteContainer } from './QuoteContainer';
import { ListContainer } from './ListContainer';
import { PartnersListContainer } from './PartnersListContainer';
import { EmbedContainer } from './EmbedContainer';
import { RelatedItemsContainer } from './RelatedItemsContainer';
import { IframeContainer } from './IframeContainer';
import { PeopleContainer } from './PeopleContainer';
import { TabsContainer } from './TabsContainer';
import { HeadingContainer } from './HeadingContainer';
import { CardsContainer } from './CardsContainer';
import { OneColumnContainer } from './OneColumnContainer';
import { DescriptiveListContainer } from './DescriptiveListContainer';
import { ImageListContainer } from './ImageListContainer';

interface IProps {
  data: SliceBody[];
  isColumnLayout?: boolean; // for column style pages, segment needs specific styles
}

export const Slices = ({ data = [], isColumnLayout = false }: IProps) => {
  return (
    <>
      {data.map((s: SliceBody, i: number) => {
        const key = `slice-${s.type}-${i}`;

        switch (s.type) {
          case 'text':
            s = s as SliceText;
            return (
              <Segment
                key={key}
                isColumnLayout={isColumnLayout}
                separator={s.primary?.separator ?? false}
                container={!isColumnLayout}
              >
                <OneColumnContainer
                  isColumnLayout={isColumnLayout}
                  text={s.primary?.text ?? null}
                />
              </Segment>
            );
          case 'richtext':
            s = s as SliceRichtext;
            return (
              <Segment
                key={key}
                isColumnLayout={isColumnLayout}
                separator={s.primary?.separator ?? false}
                container={!isColumnLayout}
              >
                <TwoColumnContainer
                  lead={s.primary?.lead ?? (null as PrismicRichText)}
                  column_a={
                    s.primary?.column_a && s.primary?.column_a[0].text.length > 0
                      ? s.primary?.column_a
                      : null
                  }
                  column_b={
                    s.primary?.column_b && s.primary?.column_b[0].text.length > 0
                      ? s.primary?.column_b
                      : null
                  }
                  isColumnLayout={isColumnLayout}
                />
              </Segment>
            );
          case 'single_image':
            s = s as SliceSingleImage;
            return (
              <Segment
                key={key}
                isColumnLayout={isColumnLayout}
                separator={s.primary?.separator ?? false}
              >
                <ImageContainer
                  image={s.primary?.image ?? (null as PrismicImage)}
                  caption={s.primary?.caption ?? (null as PrismicRichText)}
                  isColumnLayout={isColumnLayout}
                />
              </Segment>
            );
          case 'image_gallery':
            s = s as SliceImageGallery;
            return (
              <Segment
                key={key}
                isColumnLayout={isColumnLayout}
                separator={s.primary?.separator ?? false}
                separatorOffset={s.primary?.display !== 'Horizontal'}
              >
                <GalleryContainer
                  isColumnLayout={isColumnLayout}
                  fields={s.fields ?? []}
                  display={s.primary?.display ?? ''}
                />
              </Segment>
            );
          case 'video':
            s = s as SliceVideo;
            return (
              <Segment
                key={key}
                isColumnLayout={isColumnLayout}
                separator={s.primary?.separator ?? false}
                separatorOffset={false}
              >
                <VideoContainer
                  video={s.primary?.video ?? (null as PrismicVideoEmbed)}
                  caption={s.primary?.caption ?? (null as PrismicRichText)}
                  credits={s.primary?.credits ?? (null as PrismicRichText)}
                />
              </Segment>
            );
          case 'cta':
            s = s as SliceCta;
            return (
              <Segment
                key={key}
                isColumnLayout={isColumnLayout}
                separator={s.primary?.separator ?? false}
                container={!isColumnLayout}
              >
                <CtaContainer
                  heading={s.primary?.heading ?? (null as PrismicRichText)}
                  text={s.primary?.text ?? (null as PrismicRichText)}
                  linkText={s.primary?.link_text ?? (null as PrismicRichText)}
                  link={(s.primary?.link as LinkableFragment) ?? (null as PrismicLink)}
                  image={s.primary?.cta_image ?? (null as PrismicImage)}
                  isColumnLayout={isColumnLayout}
                />
              </Segment>
            );
          case 'quote':
            s = s as SliceQuote;
            return (
              <Segment
                key={key}
                separator={s.primary?.separator ?? false}
                isColumnLayout={isColumnLayout}
                container={!isColumnLayout}
              >
                <QuoteContainer
                  quote={s.primary?.quote ?? (null as PrismicRichText)}
                  citation={s.primary?.citation ?? (null as PrismicRichText)}
                  isColumnLayout={isColumnLayout}
                />
              </Segment>
            );
          case 'list':
            s = s as SliceList;
            return (
              <Segment
                separator={s.primary?.separator ?? true}
                key={key}
                isColumnLayout={isColumnLayout}
                container={!isColumnLayout}
              >
                <ListContainer
                  title={s.primary?.list_title ?? (null as PrismicRichText)}
                  image={s.primary?.list_image ?? (null as PrismicImage)}
                  caption={s.primary?.list_image_caption ?? (null as PrismicRichText)}
                  items={s.fields ?? ([] as PrismicBodyListFields[])}
                  isColumnLayout={isColumnLayout}
                />
              </Segment>
            );
          case 'descriptive_list':
            s = s as SliceDescriptiveList;
            return (
              <Segment
                separator={s.primary?.separator ?? true}
                key={key}
                isColumnLayout={isColumnLayout}
                container={!isColumnLayout}
              >
                <DescriptiveListContainer
                  title={s.primary?.list_title ?? (null as PrismicRichText)}
                  image={s.primary?.list_image ?? (null as PrismicImage)}
                  items={s.fields ?? ([] as PrismicBodyDescriptiveListFields[])}
                  isColumnLayout={isColumnLayout}
                />
              </Segment>
            );
          case 'embed':
            s = s as SliceEmbed;
            return (
              <Segment
                key={key}
                separator={s.primary?.separator ?? false}
                isColumnLayout={isColumnLayout}
              >
                <EmbedContainer embed={s?.primary?.embed ?? null} isColumnLayout={isColumnLayout} />
              </Segment>
            );
          case 'related_items':
            s = s as SliceRelated;

            return (
              <Segment
                key={key}
                // separator={s.primary?.related_title === null}
                separator={s.primary?.separator ?? false}
                isColumnLayout={isColumnLayout}
                separatorOffset={false}
              >
                <RelatedItemsContainer
                  title={s.primary?.related_title ?? (null as PrismicRichText)}
                  items={s.fields ?? ([] as PrismicRelatedItemsFields[])}
                  isColumnLayout={isColumnLayout}
                />
              </Segment>
            );
          case 'people':
            s = s as SlicePeople;

            return (
              <Segment
                key={key}
                separator={s.primary?.separator ?? false}
                isColumnLayout={isColumnLayout}
                separatorOffset={false}
              >
                <PeopleContainer
                  title={s.primary?.people_title ?? (null as PrismicRichText)}
                  images={s.primary?.people_image as boolean}
                  items={s.fields ?? ([] as PrismicPeopleFields[])}
                  color={mapColor(s.primary?.placeholder_color)}
                  isColumnLayout={isColumnLayout}
                />
              </Segment>
            );
          case 'iframe':
            s = s as SliceIframe;

            return (
              <Segment
                key={key}
                separator={s.primary?.separator ?? false}
                isColumnLayout={isColumnLayout}
                container={!isColumnLayout}
              >
                <IframeContainer
                  title={s.primary?.iframe_title ?? (null as PrismicRichText)}
                  link={(s.primary?.link as LinkableFragment) ?? (null as PrismicLink)}
                  width={s.primary?.width ?? undefined}
                  height={s.primary?.height ?? undefined}
                />
              </Segment>
            );
          case 'tabs':
            s = s as SliceTabs;
            return (
              <Segment
                key={key}
                separator={s.primary?.separator ?? false}
                isColumnLayout={isColumnLayout}
                separatorOffset={false}
                container={!isColumnLayout}
              >
                <TabsContainer
                  title={s.primary?.tabs_title ?? (null as PrismicRichText)}
                  items={s.fields ?? ([] as PrismicTabsFields[])}
                  isColumnLayout={isColumnLayout}
                />
              </Segment>
            );
          case 'heading':
            s = s as SliceHeading;
            return (
              <Segment
                key={key}
                separator={s.primary?.separator ?? false}
                noSpacing
                container={!isColumnLayout}
              >
                <HeadingContainer
                  heading={s.primary?.heading ?? (null as PrismicRichText)}
                  isColumnLayout={isColumnLayout}
                />
              </Segment>
            );
          case 'cards':
            s = s as SliceCards;
            const columns =
              s.primary?.column_number === 2 || s.primary?.column_number === 3
                ? s.primary?.column_number
                : 2;

            return (
              <Segment
                key={key}
                separator={s.primary?.separator ?? false}
                isColumnLayout={isColumnLayout}
                separatorOffset={false}
                container={!isColumnLayout}
              >
                <CardsContainer
                  columns={columns}
                  isColumnLayout={isColumnLayout}
                  items={s.fields ?? ([] as PrismicCardsFields[])}
                />
              </Segment>
            );
          case 'image_grid':
            s = s as SliceImageGrid;

            return (
              <Segment
                key={key}
                separator={s.primary?.separator ?? false}
                isColumnLayout={isColumnLayout}
                separatorOffset={false}
                container={!isColumnLayout}
              >
                <ImageListContainer
                  isColumnLayout={isColumnLayout}
                  heading={s.primary?.title1 ?? (null as PrismicRichText)}
                  items={s.fields ?? ([] as PrismicImageGridFields[])}
                />
              </Segment>
            );
            case 'partners_list':
              s = s as SlicePartnersList;
              return (
                <Segment
                  separator={s.primary?.separator ?? true}
                  key={key}
                  isColumnLayout={isColumnLayout}
                  container={!isColumnLayout}
                >
                  <PartnersListContainer
                    title={s.primary?.partners_title ?? (null as PrismicRichText)}
                    items={s.fields ?? ([] as PrismicBodyPartnersListFields[])}
                    isColumnLayout={isColumnLayout}
                  />
                </Segment>
              );
          default:
            return null;
        }
      })}
    </>
  );
};
